import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DONATE_URL } from "@/config";
import { Button } from "@/components/ui/button";
import { HeartHandshake, Wrench, Cpu, Users, Sprout, LineChart, Heart, ArrowRight } from "lucide-react";

const FUNDING = [
  { icon: HeartHandshake, title: "Sponsored access", desc: "Covering membership for people who are ready to quit but can't afford it." },
  { icon: Wrench, title: "Recovery tools & content", desc: "Building and improving the coping tools, plans, and Scripture that carry people through." },
  { icon: Cpu, title: "Technology & AI costs", desc: "Keeping the Companion and the app running, safely and reliably." },
  { icon: Users, title: "Community outreach", desc: "Reaching more people who are looking for a faith-centered way to break free." },
  { icon: Sprout, title: "Program development", desc: "Growing Not By Will thoughtfully, without turning it into just another subscription app." },
  { icon: LineChart, title: "Measuring outcomes", desc: "Learning what truly helps people quit — and doing more of it." },
];

const GIVING = [
  { amount: "$5", label: "Help provide one month of access" },
  { amount: "$40", label: "Help provide one year of access" },
  { amount: "$100", label: "Support multiple recovery journeys" },
  { amount: "$500", label: "Community Supporter" },
];

export default function MissionPage() {
  const navigate = useNavigate();

  const give = () => {
    if (DONATE_URL) window.open(DONATE_URL, "_blank", "noopener,noreferrer");
    else toast("Giving is opening soon — thank you for your heart to help.");
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="mission-page">
      <div className="rounded-3xl bg-brand-navy text-brand-ivory border border-brand-gold/20 p-8 sm:p-10 shadow-soft">
        <p className="uppercase tracking-[0.2em] text-[11px] text-gold mb-3">Our Mission</p>
        <h1 className="font-serif text-3xl sm:text-4xl leading-tight mb-4">Freedom shouldn't depend on what you can afford.</h1>
        <p className="text-brand-ivory-muted leading-relaxed">
          Not By Will exists to help people break free from nicotine dependence through evidence-informed behavior
          change, practical recovery tools, and faith-centered support. Memberships and gifts help us keep developing
          Not By Will while making the full recovery experience available to people who otherwise couldn't afford it.
        </p>
      </div>

      {/* Help someone break the chain */}
      <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="support-ministry">
        <div className="rounded-xl bg-sage p-2.5 w-fit mb-3"><Heart className="w-5 h-5 text-brand-teal-dark" /></div>
        <h2 className="font-serif text-2xl text-olive mb-1">Help someone break the chain</h2>
        <p className="text-olive/70 mb-6 max-w-2xl">
          Your gift helps make faith-based nicotine recovery accessible to people who are ready to quit — regardless
          of their ability to pay.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {GIVING.map((g) => (
            <button key={g.amount} data-testid={`give-${g.amount.replace("$", "")}`} onClick={give}
              className="flex items-center gap-4 rounded-2xl border border-border p-4 text-left transition-colors hover:bg-sage hover:border-brand-teal/40">
              <span className="font-serif text-2xl text-brand-teal-dark w-16 shrink-0">{g.amount}</span>
              <span className="text-sm text-olive/80">{g.label}</span>
            </button>
          ))}
          <button data-testid="give-other" onClick={give}
            className="flex items-center gap-4 rounded-2xl border border-border p-4 text-left transition-colors hover:bg-sage hover:border-brand-teal/40 sm:col-span-2">
            <span className="font-serif text-lg text-brand-teal-dark w-16 shrink-0">Other</span>
            <span className="text-sm text-olive/80">Give another amount — one time or recurring, where supported</span>
          </button>
        </div>
        <Button data-testid="support-give-btn" onClick={give}
          className="mt-5 rounded-full bg-terracotta hover:bg-terracotta-soft text-white gap-2 min-h-[48px] px-7">
          <Heart className="w-4 h-4" /> Support this ministry
        </Button>
        <p className="text-xs text-stone mt-4">
          Giving is separate from membership and is never required. Gifts support the mission below.
        </p>
      </div>

      {/* Where support goes */}
      <div>
        <h2 className="font-serif text-2xl text-olive mb-4">Where your support goes</h2>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="funding-areas">
          {FUNDING.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-3xl bg-card border border-border p-6 shadow-soft">
                <div className="rounded-xl bg-sage p-2.5 w-fit mb-3"><Icon className="w-5 h-5 text-brand-teal-dark" /></div>
                <p className="font-serif text-lg text-olive mb-1">{f.title}</p>
                <p className="text-sm text-olive/70">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl bg-sage/60 border border-border p-6 text-center">
        <p className="text-olive/80 mb-4">Want the deeper experience for yourself?</p>
        <Button data-testid="mission-membership-link" onClick={() => navigate("/app/membership")} variant="outline"
          className="rounded-full border-olive/20 text-olive hover:bg-card gap-2 min-h-[46px] px-6">
          Continue your journey <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
